// src/services/enrollmentPeriods.js
//
// The period a set of enrollment counts belongs to — the calendar month, or the
// ISO week — expressed as a stable key.
//
// WHY THIS EXISTS. Enrollment counts used to be one running number per
// coordinator with no period attached at all. A month only got named when an
// admin clicked "Save & Reset", and it was named from `new Date()` at the
// moment of the click — so archiving August's numbers on 1 September filed them
// as September. A manager back from leave on the 1st had nowhere to put the
// previous month's figures, because the previous month did not exist as a thing
// the data could describe.
//
// Making the period a KEY rather than a side effect of when somebody clicked
// fixes that, and removes the monthly reset ritual entirely: a new period starts
// empty because nothing has been written to it yet. There is nothing to forget
// and nothing to double-click.
//
// FIREBASE-FREE ON PURPOSE. This is all arithmetic on dates, which is precisely
// the part worth testing, so it must be testable without Firestore. The I/O half
// lives in the components and the path helpers.

/** Zero-pad to two digits. */
const pad2 = (n) => String(n).padStart(2, "0");

export const PERIOD_TYPES = Object.freeze({ MONTH: "month", WEEK: "week" });

// ---------------------------------------------------------------------------
// Month periods — "YYYY-MM"
// ---------------------------------------------------------------------------

/** Month key for a Date. Local time, because a coordinator's month is local. */
export const monthKey = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
};

/** "August 2026" from "2026-08". Empty string for anything unparseable. */
export const monthLabel = (key) => {
  const m = /^(\d{4})-(\d{2})$/.exec(String(key || ""));
  if (!m) return "";
  const year = Number(m[1]);
  const month = Number(m[2]);
  if (month < 1 || month > 12) return "";
  return new Date(year, month - 1, 1).toLocaleString(undefined, {
    month: "long",
    year: "numeric",
  });
};

/** Step a month key by n months (negative goes back). "" if unparseable. */
export const shiftMonth = (key, n) => {
  const m = /^(\d{4})-(\d{2})$/.exec(String(key || ""));
  if (!m) return "";
  const d = new Date(Number(m[1]), Number(m[2]) - 1 + Number(n || 0), 1);
  return monthKey(d);
};

// ---------------------------------------------------------------------------
// Week periods — "YYYY-Www", ISO-8601
// ---------------------------------------------------------------------------
//
// ISO weeks, not "week of the year counted from 1 January". A week belongs to
// the year containing its Thursday, which is why the first days of January can
// legitimately be week 52 or 53 of the PREVIOUS year. Getting this wrong would
// silently file a week's counts under the wrong year every few years, so it is
// worth the handful of lines to do properly.

/** The Thursday of the ISO week containing `date`. */
const isoThursday = (date) => {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  // getDay(): 0=Sun..6=Sat. ISO days run Mon=1..Sun=7.
  const isoDay = d.getDay() === 0 ? 7 : d.getDay();
  d.setDate(d.getDate() + 4 - isoDay);
  return d;
};

/** ISO week key for a Date, e.g. "2026-W36". */
export const weekKey = (date = new Date()) => {
  const src = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(src.getTime())) return "";
  const thu = isoThursday(src);
  const yearStart = new Date(thu.getFullYear(), 0, 1);
  const week = Math.ceil(((thu - yearStart) / 86400000 + 1) / 7);
  return `${thu.getFullYear()}-W${pad2(week)}`;
};

/** The Monday that starts an ISO week key. null if unparseable. */
export const weekStart = (key) => {
  const m = /^(\d{4})-W(\d{2})$/.exec(String(key || ""));
  if (!m) return null;
  const year = Number(m[1]);
  const week = Number(m[2]);
  if (week < 1 || week > 53) return null;
  // 4 January is always in ISO week 1.
  const jan4 = new Date(year, 0, 4);
  const jan4Iso = jan4.getDay() === 0 ? 7 : jan4.getDay();
  const week1Monday = new Date(year, 0, 4 - (jan4Iso - 1));
  return new Date(
    week1Monday.getFullYear(),
    week1Monday.getMonth(),
    week1Monday.getDate() + (week - 1) * 7
  );
};

/** "1–7 Sep 2026" from "2026-W36". Empty string if unparseable. */
export const weekLabel = (key) => {
  const start = weekStart(key);
  if (!start) return "";
  const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
  const sameMonth = start.getMonth() === end.getMonth();
  const startTxt = start.toLocaleString(undefined, {
    day: "numeric",
    ...(sameMonth ? {} : { month: "short" }),
  });
  const endTxt = end.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  return `${startTxt}–${endTxt}`;
};

/** Step a week key by n weeks (negative goes back). "" if unparseable. */
export const shiftWeek = (key, n) => {
  const start = weekStart(key);
  if (!start) return "";
  return weekKey(
    new Date(start.getFullYear(), start.getMonth(), start.getDate() + Number(n || 0) * 7)
  );
};

// ---------------------------------------------------------------------------
// Shared
// ---------------------------------------------------------------------------

/** Current key for a period type. */
export const currentPeriodKey = (type, date = new Date()) =>
  type === PERIOD_TYPES.WEEK ? weekKey(date) : monthKey(date);

/** Human label for a key of the given type. */
export const periodLabel = (type, key) =>
  type === PERIOD_TYPES.WEEK ? weekLabel(key) : monthLabel(key);

/** Step a key of the given type. */
export const shiftPeriod = (type, key, n) =>
  type === PERIOD_TYPES.WEEK ? shiftWeek(key, n) : shiftMonth(key, n);

/** Is this key a well-formed key of the given type? */
export const isValidPeriodKey = (type, key) =>
  type === PERIOD_TYPES.WEEK
    ? /^\d{4}-W\d{2}$/.test(String(key || "")) && Boolean(weekStart(key))
    : Boolean(monthLabel(key));

/**
 * The list a period dropdown should offer.
 *
 * Always contains the current period, even when nothing has been recorded in it
 * yet — a new month is empty by design, and it still has to be selectable.
 * Anything with data is included however old, so history never falls off the
 * end. Recent periods with no data are offered too, which is what lets somebody
 * fill in a month nobody ever opened.
 *
 * Newest first, no duplicates.
 *
 * @param {string} type PERIOD_TYPES value
 * @param {string[]} keysWithData keys that have stored counts
 * @param {{now?: Date, lookback?: number}} [opts] lookback = how many empty
 *        recent periods to offer beyond the current one
 * @returns {string[]}
 */
export const listPeriodOptions = (type, keysWithData = [], opts = {}) => {
  const now = opts.now instanceof Date ? opts.now : new Date();
  const lookback = Number.isInteger(opts.lookback) ? opts.lookback : 3;
  const current = currentPeriodKey(type, now);

  const out = new Set([current]);
  for (let i = 1; i <= Math.max(0, lookback); i += 1) {
    const k = shiftPeriod(type, current, -i);
    if (k) out.add(k);
  }
  (Array.isArray(keysWithData) ? keysWithData : []).forEach((k) => {
    if (isValidPeriodKey(type, k)) out.add(k);
  });

  // Descending. Both key formats sort correctly as strings within a type
  // because every component is zero-padded and fixed width.
  return [...out].sort((a, b) => (a < b ? 1 : a > b ? -1 : 0));
};

/**
 * Is the selected period the one we are currently in?
 *
 * Editing a past period is allowed — a manager back from leave has to be able
 * to enter last month — but it should never happen without the person knowing,
 * so the UI says so and the write is stamped.
 */
export const isCurrentPeriod = (type, key, now = new Date()) =>
  Boolean(key) && key === currentPeriodKey(type, now);
